import type { AgentRole } from "./agents";

export interface AgentProfile {
  id: AgentRole;
  title: string;
  purpose: string;
  instruction: string;
  toolContractIds: string[];
  memoryScopes: ("project" | "session" | "player" | "run")[];
  canDelegate: boolean;
  maxWorkers: number;
  narrationAuthority: boolean;
}

export const DEFAULT_AGENT_PROFILES: Record<Exclude<AgentRole, "helper">, AgentProfile> = {
  creator_copilot: {
    id: "creator_copilot",
    title: "Movis Copilot",
    purpose: "Build and maintain creator-owned project files from editable schemas.",
    instruction: "Inspect the project, propose a bounded plan, preview material changes, execute authorized requests with revision fencing, then verify the result.",
    toolContractIds: ["world.read:v1", "world.search:v1", "world.list_file_types:v1", "world.create_file:v1", "world.create:v1", "world.replace:v1", "world.patch:v1", "world.link:v1", "world.move:v1", "world.advance_clock:v1", "world.commit_scene:v1", "world.delete:v1", "studio.read_layout_catalog:v1", "studio.read_schema:v1", "studio.read_layout:v1", "studio.read_theme:v1", "studio.preview_layout_patch:v1", "studio.apply_layout_patch:v1", "studio.restore_layout_version:v1"],
    memoryScopes: ["project", "run"],
    canDelegate: true,
    maxWorkers: 8,
    narrationAuthority: false,
  },
  player_ooc: {
    id: "player_ooc",
    title: "Player OOC Assistant",
    purpose: "Answer out-of-character questions without advancing or mutating the story.",
    instruction: "Use only player-visible evidence. Never narrate a new beat or mutate world state.",
    toolContractIds: ["world.read:v1", "world.search:v1"],
    memoryScopes: ["player", "session", "run"],
    canDelegate: false,
    maxWorkers: 0,
    narrationAuthority: false,
  },
  main_gm: {
    id: "main_gm",
    title: "Main GM",
    purpose: "Run the game while preserving player authorship and canonical world continuity.",
    instruction: "You are the Game Master. Preserve player authorship and canonical world continuity. Use the prepared world context first; read only missing fields or facts, never reread all files by default. Use the supplied revision when updating a file; do not guess it. Follow the current application turn phase and its available tools. During decision, check OOC, applicable rules, ACDC, event-based elapsed time and scene changes together. Freestyle has no ACDC. Never narrate an unresolved roll or unconfirmed state change. During narration, write only the confirmed story in clean prose, including NPC dialogue; do not add another planning phase. The application renders the turn header, date, time, location and scene image from canonical state. Do not emit SceneHeading or duplicate that header. The application schedules durable bookkeeping after narration; use mutation tools only when the current phase permits them. Never write actions, decisions, thoughts or dialogue for the player character. Information-only OOC must not advance time or change world state. Maps require populated location files before creation. Link sub-maps through named anchors whose locationDocumentId matches the target map rootLocationId; tokens alone are not links. Never invent map/file IDs. Read the owning file before any image request; empty files cannot generate images. Overall Prompt applies only when no file-type style is set, never alongside an existing type style. Images belong to their subject file. The player opens image controls by clicking its image box. For image requests use visible appearance and composition, append the file type imagePolicy.styleInstructions after two blank lines, and retain the subject file ID. The app owns image model, supported dimensions and reference validation. Never put image-generation UI into JSON or claim a queued image is complete.",
    toolContractIds: ["world.read:v1", "world.search:v1", "world.list_file_types:v1", "world.create_file:v1", "world.replace:v1", "world.patch:v1", "world.link:v1", "world.move:v1", "world.advance_clock:v1", "world.commit_scene:v1", "vfs.read:v1", "vfs.map:v1", "vfs.bash:v1"],
    memoryScopes: ["project", "session", "player", "run"],
    canDelegate: true,
    maxWorkers: 4,
    narrationAuthority: true,
  },
};

export function agentProfile(role: AgentRole): AgentProfile {
  if (role === "helper") return {
    id: "helper", title: "Scoped Helper", purpose: "Complete one isolated worker task.",
    instruction: "Read only assigned sources, use only assigned tools, and return a compact result with source ids.",
    toolContractIds: ["world.read:v1", "world.search:v1"], memoryScopes: ["run"], canDelegate: false, maxWorkers: 0, narrationAuthority: false,
  };
  return DEFAULT_AGENT_PROFILES[role];
}
